<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getFrameSrc, getUrlFromList } from '@/helpers/videoUrlUtils'

const props = defineProps({
  playlistFirst: Boolean,
  title: String,
  modelValue: Boolean,
  videoSrc: String
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const preloader = ref(true)

const frameSrc = computed(() => {
  if (!props.videoSrc) return null

  return getFrameSrc(getUrlFromList(props.videoSrc))
})

watch(frameSrc, () => {
  preloader.value = true
})

function onFrameLoad() {
  preloader.value = false
}
</script>

<template>
  <v-rich-modal :title="title" :model-value="modelValue" @update:model-value="(e: boolean) => emit('update:modelValue', e)">
    <section class="public-video-modal" :class="{ 'public-video-modal_playlist-first': playlistFirst }">
      <div class="public-video-modal__video">
        <iframe :src="frameSrc ?? undefined" allowfullscreen @load="onFrameLoad" />

        <div v-if="preloader" class="public-video-modal__preloader">
          <v-icon class="mdi-spin" size="36">mdi-timer-sand</v-icon>
        </div>
      </div>

      <div class="public-video-modal__playlist">
        <slot name="playlist" />
      </div>

      <p class="public-video-modal__description">
        <slot name="description" />
      </p>

      <div class="public-video-modal__actions">
        <slot name="actions" />
      </div>
    </section>
  </v-rich-modal>
</template>

<style lang="scss">
$mobileMaxWidth: 960px;

.public-video-modal {
  max-width: 100vw;

  @include desktop {
    width: min(90vw, 1200px);

    display: grid;
    grid-template-columns: 1fr 350px;
    grid-template-rows: 1fr minmax(0, auto) minmax(0, auto);
    gap: 16px 32px;
    grid-template-areas:
      'video playlist'
      'description playlist'
      'actions playlist';

    &__video {
      grid-area: video;
    }

    &__playlist {
      grid-area: playlist;
    }

    &__description {
      grid-area: description;
    }

    &__actions {
      grid-area: actions;
    }

    &__playlist {
      height: 0;
    }
  }

  @include mobile {
    display: flex;
    flex-direction: column;

    & > * {
      margin-bottom: 16px;
    }

    &__video {
      order: 1;
    }

    &__description {
      order: 2;
    }

    &__actions {
      order: 3;
    }

    &__playlist {
      order: 4;
    }

    &_playlist-first > &__playlist {
      order: 2;
    }

    &_playlist-first > &__actions {
      order: 3;
    }

    &_playlist-first > &__description {
      order: 4;
    }
  }

  &__video {
    position: relative;
    overflow: hidden;
    width: 100%;
    padding-top: 56.25%;
    /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
    height: 0;

    & > iframe {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
  }

  &__video > &__preloader {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;

    background: black;
    color: white;

    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
